/**
 * The assistant panel remounts when the shell swaps (Deals list vs a deal).
 * React state does not survive that. This snapshot does, including a turn
 * that was still streaming when the panel went away.
 *
 * The snapshot is confidential. It is stored per user and org, and logout
 * removes every copy in this tab.
 */
import { stoppedTurnAfterDetach } from './assistantTabs.js';

export const LIVE_THREAD_PREFIX = 'd2d.assistant.liveThread';
/** Older builds used one shared key. It is never read. */
export const LEGACY_LIVE_THREAD_KEY = 'd2d.assistant.liveThread';

const OWNER_PART = /^[A-Za-z0-9._:-]{1,80}$/;
const memory = new Map();

export function liveThreadOwner(user) {
  if (!user || user.id == null || user.id === '') return null;
  const userId = String(user.id).trim();
  const orgId = String(user.org_id ?? user.orgId ?? '').trim();
  if (!OWNER_PART.test(userId) || !OWNER_PART.test(orgId)) return null;
  return { userId, orgId };
}

export function liveThreadStorageKey(owner) {
  const userId = String(owner?.userId ?? '').trim();
  const orgId = String(owner?.orgId ?? '').trim();
  if (!OWNER_PART.test(userId) || !OWNER_PART.test(orgId)) return null;
  return `${LIVE_THREAD_PREFIX}.${orgId}.${userId}`;
}

function dropLegacyKey() {
  try {
    sessionStorage.removeItem(LEGACY_LIVE_THREAD_KEY);
  } catch {
    // sessionStorage is absent in node tests and private mode.
  }
}

function readStored(key) {
  if (!key) return null;
  if (memory.has(key)) return memory.get(key);
  try {
    const raw = sessionStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed) memory.set(key, parsed);
    return parsed;
  } catch {
    return null;
  }
}

function writeStored(key, snapshot) {
  if (!key) return;
  if (snapshot) memory.set(key, snapshot);
  else memory.delete(key);
  try {
    if (!snapshot) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, JSON.stringify(snapshot));
  } catch {
    // Private mode can reject the write. The module copy still covers a remount.
  }
}

export function clearLiveThread(owner) {
  const key = liveThreadStorageKey(owner);
  if (key) writeStored(key, null);
  dropLegacyKey();
}

/** Logout: this tab must not keep any desk transcript for the next sign-in. */
export function clearAllLiveThreads() {
  memory.clear();
  try {
    const keys = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (!key) continue;
      if (key === LEGACY_LIVE_THREAD_KEY || key.startsWith(`${LIVE_THREAD_PREFIX}.`)) keys.push(key);
    }
    keys.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // No sessionStorage. The map clear above is the in-memory copy.
  }
}

/** Mid-stream snapshot. `visibleLane` is the lane the user is looking at, not the raw server tab. */
export function inFlightLiveSnapshot({ messages, question, partial, visibleLane, tab }) {
  return {
    messages,
    inFlight: true,
    question: question || '',
    partial: partial || '',
    channel: visibleLane === 'guides' ? 'guides' : 'ask',
    tab: tab === 'guides' ? 'guides' : 'ask',
  };
}

export function saveLiveThread(owner, snapshot) {
  const key = liveThreadStorageKey(owner);
  if (!key) return null;
  dropLegacyKey();
  const messages = Array.isArray(snapshot?.messages) ? snapshot.messages : [];
  const previous = readStored(key);
  if (!messages.length && !snapshot?.inFlight && previous?.messages?.length) return previous;
  const next = {
    userId: String(owner.userId),
    orgId: String(owner.orgId),
    messages,
    inFlight: Boolean(snapshot?.inFlight),
    question: snapshot?.question || '',
    partial: snapshot?.partial || '',
    channel: snapshot?.channel === 'guides' ? 'guides' : 'ask',
    tab: snapshot?.tab || 'ask',
  };
  writeStored(key, next);
  return next;
}

/** Close an in-flight snapshot into a stopped turn the next mount can show. */
export function messagesFromSnapshot(snapshot) {
  if (!snapshot) return [];
  const messages = (Array.isArray(snapshot.messages) ? snapshot.messages : []).map((message) => ({
    ...message,
  }));
  if (!snapshot.inFlight) return messages.filter((message) => message.meta !== 'streaming');
  const channel = snapshot.channel === 'guides' ? 'guides' : 'ask';
  const question =
    snapshot.question ||
    [...messages].reverse().find((message) => message.role === 'user')?.content ||
    '';
  const partial =
    snapshot.partial ||
    [...messages].reverse().find((message) => message.meta === 'streaming')?.content ||
    '';
  const kept = messages.filter((message) => message.meta !== 'streaming');
  const hasQuestion = kept.some((message) => message.role === 'user' && message.content === question);
  const base = hasQuestion || !question ? kept : [...kept, { role: 'user', content: question, channel }];
  return [...base, { ...stoppedTurnAfterDetach({ question, partial }), channel }];
}

export function loadLiveThread(owner) {
  const key = liveThreadStorageKey(owner);
  if (!key) return null;
  dropLegacyKey();
  const snap = readStored(key);
  if (!snap || String(snap.userId) !== String(owner.userId) || String(snap.orgId) !== String(owner.orgId)) {
    return null;
  }
  const messages = messagesFromSnapshot(snap);
  if (snap.inFlight && messages.length) {
    saveLiveThread(owner, { ...snap, messages, inFlight: false, partial: '' });
  }
  return { ...snap, messages, inFlight: false };
}

/**
 * Body posted when a turn is stopped. Callers pass the current ref values so an
 * unmount cleanup from the first render still writes the open conversation.
 */
export function stoppedTurnAppendBody({ question, answer, dashboardKey, conversationId, channel }) {
  return {
    question: question || '',
    answer: answer || '',
    dashboard_key: dashboardKey || '',
    conversation_id: conversationId || null,
    assistant_meta: 'stopped',
    channel: channel === 'guides' ? 'guides' : 'ask',
  };
}

/**
 * A server hydrate must not replace a turn that never reached the thread.
 * An empty reconnect is how a remount was showing the greeting.
 */
export function mergeHydratedMessages(serverMessages, localMessages) {
  const server = Array.isArray(serverMessages) ? serverMessages : [];
  const local = Array.isArray(localMessages) ? localMessages : [];
  if (!local.length) return server;
  const lastUser = [...local].reverse().find((message) => message.role === 'user');
  const serverHasUser =
    lastUser && server.some((message) => message.role === 'user' && message.content === lastUser.content);
  const stopped = local.some((message) => message.meta === 'stopped' || message.retryQuestion);
  if (stopped || (lastUser && !serverHasUser)) return local;
  return server.length ? server : local;
}
